import { useState } from "react";
import { Button, CheckSmall, Input, WithActions, PlusSmall } from "components";
import { Modal } from "features/Modal";
import { Language } from "helpers";
import { useUpdateParkingMutation } from "services";
import { PaymentAmenitiesStep } from "./components/PaymentAmenitiesStep";
import { TypeSlotsStep } from "./components/TypeSlotsStep";
import { MunicipalityGeoStep } from "./components";
import { t } from "i18next";

type ModalStep =
  | "name_mk"
  | "name_al"
  | "name_en"
  | "municipality_geo"
  | "type_slots"
  | "payment_amenities"
  | "success"
  | "error";

interface FormData {
  parking_name_mk: string;
  parking_name_al: string;
  parking_name_en: string;
  longitude: string;
  latitude: string;
  municipality_id: number;
  area_type: string;
  total_parking_slots: number;
  payment_options: number[];
  amenities: number[];
  address: string;
}

interface ParkingAddModalProps {
  open: boolean;
  setOpen: (value: boolean) => void;
  parkingId?: string;
  initialData?: Partial<FormData>;
  onSuccess?: () => void;
}

export const ParkingAddModal: React.FC<ParkingAddModalProps> = ({
  open,
  setOpen,
  parkingId,
  initialData,
  onSuccess,
}) => {
  const [step, setStep] = useState<ModalStep>("name_mk");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [language, setLanguage] = useState<Language>("MK");
  const [form, setForm] = useState<FormData>({
    parking_name_mk: initialData?.parking_name_mk || "",
    parking_name_al: initialData?.parking_name_al || "",
    parking_name_en: initialData?.parking_name_en || "",
    longitude: initialData?.longitude || "",
    latitude: initialData?.latitude || "",
    municipality_id: initialData?.municipality_id || 0,
    area_type: initialData?.area_type || "",
    total_parking_slots: initialData?.total_parking_slots || 0,
    payment_options: initialData?.payment_options || [],
    amenities: initialData?.amenities || [],
    address: initialData?.address || "",
  });

  const [updateParking, { isLoading }] = useUpdateParkingMutation();

  const handleChange = (key: keyof FormData, value: string | number | number[]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const nextStep = () => {
    switch (step) {
      case "name_mk":
        if (!form.parking_name_mk.trim()) {
          setErrorMessage(t("parkingManagement.parkingNameMacedonianRequired"));
          return;
        }
        setErrorMessage(null);
        setStep("name_al");
        break;
      case "name_al":
        if (!form.parking_name_al.trim()) {
          setErrorMessage(t("parkingManagement.parkingNameAlbanianRequired"));
          return;
        }
        setErrorMessage(null);
        setStep("name_en");
        break;
      case "name_en":
        if (!form.parking_name_en.trim()) {
          setErrorMessage(t("parkingManagement.parkingNameEnglishRequired"));
          return;
        }
        setErrorMessage(null);
        setStep("municipality_geo");
        break;
      case "municipality_geo":
        if (!form.municipality_id || !form.latitude.trim() || !form.longitude.trim()) {
          setErrorMessage(t("parkingManagement.municipalityIdLatitudeLongtitudeRequired"));
          return;
        }
        setErrorMessage(null);
        setStep("type_slots");
        break;
      case "type_slots":
        if (!form.area_type || !form.total_parking_slots) {
          setErrorMessage(t("parkingManagement.parkingTypeAndSlotsRequired"));
          return;
        }
        setErrorMessage(null);
        setStep("payment_amenities");
        break;
      case "payment_amenities":
        setErrorMessage(null);
        handleSubmit();
        break;
    }
  };

  const prevStep = () => {
    switch (step) {
      case "name_al":
        setStep("name_mk");
        break;
      case "name_en":
        setStep("name_al");
        break;
      case "municipality_geo":
        setStep("name_en");
        break;
      case "type_slots":
        setStep("municipality_geo");
        break;
      case "payment_amenities":
        setStep("type_slots");
        break;
      case "success":
      case "error":
        setStep("payment_amenities");
        break;
    }
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        parking_id: parkingId || "",
        lang: language,
        data: {
          ...form,
          municipality_id: Number(form.municipality_id),
          total_parking_slots: Number(form.total_parking_slots),
          payment_options: form.payment_options.map(Number).filter((n) => !isNaN(n)),
          amenities: form.amenities.map(Number).filter((n) => !isNaN(n)),
        },
      };

      await updateParking(payload).unwrap();
      setStep("success");
      if (onSuccess) onSuccess();
    } catch (error: any) {
      setStep("error");
      setErrorMessage(error?.message || "Failed to add parking");
    }
  };

  const onClose = () => {
    setOpen(false);
    setStep("name_mk");
    setErrorMessage(null);
  };

  const getParkingNameByLanguage = () => {
    switch (language) {
      case "MK":
        return form.parking_name_mk;
      case "AL":
        return form.parking_name_al;
      case "EN":
        return form.parking_name_en;
      default:
        return form.parking_name_mk;
    }
  };

  return (
    <Modal open={open} setOpen={setOpen} withClose={step === "name_mk"} onlyCloseButton>
      <div className="flex flex-col gap-6 p-4">
        {step === "name_mk" && (
          <div>
            <h3 className="mb-4 text-xs font-light uppercase text-primary">
              {t("parkingManagement.configureParking")}{" "}
              <span className="font-bold">({form.parking_name_mk || "PARKING NAME"})</span>
            </h3>
            <h3 className="text-md mb-2 mt-4 font-bold">{t("parkingManagement.parkingNameInMacedonian")}</h3>
            <div className="flex items-center">
              <Input
                value={form.parking_name_mk}
                onChange={(e) => handleChange("parking_name_mk", e.target.value)}
                placeholder={t("parkingManagement.parkingNameMk")}
                className="border-[#1C67AD] font-bold text-[#1C67AD] placeholder-[#1C67AD]"
                type="text"
                variant="modal"
              />
              <div className="bg-primary p-4">
                <PlusSmall className="text-white" />
              </div>
            </div>
            {errorMessage && <p className="mt-1 text-xs text-red-500">{errorMessage}</p>}
            <WithActions
              closeButton={t("common.cancel")}
              setOpen={onClose}
              additionalButtons={
                <Button onClick={nextStep} disabled={isLoading}>
                  {t("common.continue")}
                </Button>
              }
            />
          </div>
        )}

        {step === "name_al" && (
          <div>
            <h3 className="text-xs font-light uppercase text-primary">
              {t("parkingManagement.configureParking")} ({form.parking_name_al || "PARKING NAME"})
            </h3>
            <h3 className="text-md mb-2 mt-4 font-bold">{t("parkingManagement.parkingNameInAlbanian")}</h3>
            <div className="flex items-center">
              <Input
                value={form.parking_name_al}
                onChange={(e) => handleChange("parking_name_al", e.target.value)}
                placeholder={t("parkingManagement.parkingNameAl")}
                className="border-[#1C67AD] font-bold text-[#1C67AD] placeholder-[#1C67AD]"
                type="text"
                variant="modal"
              />
              <div className="bg-primary p-4">
                <PlusSmall className="text-white" />
              </div>
            </div>
            {errorMessage && <p className="mt-1 text-xs text-red-500">{errorMessage}</p>}
            <WithActions
              closeButton={t("common.cancel")}
              setOpen={onClose}
              additionalButtons={
                <>
                  <Button onClick={prevStep}>Back</Button>
                  <Button onClick={nextStep} disabled={isLoading}>
                    {t("common.continue")}
                  </Button>
                </>
              }
            />
          </div>
        )}

        {step === "name_en" && (
          <div>
            <h3 className="text-xs font-light uppercase text-primary">
              {t("parkingManagement.configureParking")} ({form.parking_name_en || "PARKING NAME"})
            </h3>
            <h3 className="text-md mb-2 mt-4 font-bold">{t("parkingManagement.parkingNameInEnglish")}</h3>
            <div className="flex items-center">
              <Input
                value={form.parking_name_en}
                onChange={(e) => handleChange("parking_name_en", e.target.value)}
                placeholder={t("parkingManagement.parkingNameEn")}
                className="border-[#1C67AD] font-bold text-[#1C67AD] placeholder-[#1C67AD]"
                type="text"
                variant="modal"
              />
              <div className="bg-primary p-4">
                <PlusSmall className="text-white" />
              </div>
            </div>
            {errorMessage && <p className="mt-1 text-xs text-red-500">{errorMessage}</p>}
            <WithActions
              closeButton="Cancel"
              setOpen={onClose}
              additionalButtons={
                <>
                  <Button onClick={prevStep}>{t("common.back")}</Button>
                  <Button onClick={nextStep} disabled={isLoading}>
                    {t("common.continue")}
                  </Button>
                </>
              }
            />
          </div>
        )}

        {step === "municipality_geo" && (
          <MunicipalityGeoStep
            form={form}
            handleChange={handleChange}
            errorMessage={errorMessage}
            onClose={onClose}
            prevStep={prevStep}
            nextStep={nextStep}
            isLoading={isLoading}
            getParkingNameByLanguage={getParkingNameByLanguage}
          />
        )}

        {step === "type_slots" && (
          <TypeSlotsStep
            form={form}
            handleChange={handleChange}
            errorMessage={errorMessage}
            onClose={onClose}
            prevStep={prevStep}
            nextStep={nextStep}
            isLoading={isLoading}
            getParkingNameByLanguage={getParkingNameByLanguage}
          />
        )}

        {step === "payment_amenities" && (
          <PaymentAmenitiesStep
            form={form}
            handleChange={handleChange}
            errorMessage={errorMessage}
            onClose={onClose}
            prevStep={prevStep}
            nextStep={nextStep}
            isLoading={isLoading}
            getParkingNameByLanguage={getParkingNameByLanguage}
          />
        )}

        {step === "success" && (
          <div className="text-center">
            <h3 className="text-lg font-semibold">{t("parkingManagement.parkingAddedSuccessfully")}</h3>
            <div className="mt-5 flex flex-col items-center gap-2">
              <Button onClick={onClose}>{t("common.close")}</Button>
            </div>
          </div>
        )}

        {step === "error" && (
          <div className="text-center">
            <h3 className="text-lg font-semibold text-red-500">{t("parkingManagement.errorAddingParking")}</h3>
            <p>{errorMessage}</p>
            <Button onClick={onClose}>{t("common.close")}</Button>
          </div>
        )}
      </div>
    </Modal>
  );
};
